import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Checkbox, FormControlLabel, Stack, TextField } from '@mui/material'
import { useNotificationContext } from 'units/notifications/NotificationContext'
import AppFormContainer from 'components/forms/AppFormContainer'
import FetchContext from '../../context/fetch/FetchContext'
import Page from '../pages/Page'
import Section from '../containers/Section'

const NO_FIRM = ''

export default function UserPage() {
    const { userId } = useParams()

    const fetcher = useContext(FetchContext)
    const queryClient = useQueryClient()
    const { addNotification } = useNotificationContext()

    const userQueryKey = ['users', userId]
    const firmsQueryKey = ['firms']

    const { data: user, isFetching } = useQuery({
        queryKey: userQueryKey,
        queryFn: () => fetcher.fetchOneUser(userId),
        enabled: fetcher.isAuthenticated(),
    })

    const { data: firms } = useQuery({
        queryKey: firmsQueryKey,
        queryFn: () => fetcher.fetchFirms(),
        enabled: fetcher.isAuthenticated(),
    })

    const [blocked, setBlocked] = useState(false)
    const [firmId, setFirmId] = useState(NO_FIRM)

    useEffect(() => {
        if (user != null) {
            setBlocked(!!user.blocked)
            setFirmId(user.firmId ?? NO_FIRM)
        }
    }, [user])

    const updateMutation = useMutation({
        mutationFn: (input) => fetcher.authenticatedFetch({
            method: 'put',
            path: `/users/${userId}`,
            data: { data: input },
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: userQueryKey })
            addNotification({ type: 'success', content: 'המשתמש נשמר בהצלחה' })
        },
        onError: (error) => {
            addNotification({ type: 'error', content: error?.message })
        },
    })

    const save = () => {
        updateMutation.mutate({
            blocked,
            firmId: firmId === NO_FIRM ? null : Number(firmId),
        })
    }

    const commands = [
        {
            label: 'שמירה',
            position: 'start',
            color: 'primary',
            disabled: updateMutation.isPending,
            onClick: save,
        },
    ]

    return (
        <Page
            commands={commands}
            className="user"
            title={user?.username ?? 'משתמש'}
            busy={isFetching || updateMutation.isPending}
        >
            <AppFormContainer>
                <Section title="פרטי המשתמש" xs={12} md={6}>
                    <Stack spacing={2}>
                        <TextField
                            id="user-username"
                            label="שם משתמש"
                            value={user?.username ?? ''}
                            disabled
                        />
                        <TextField
                            id="user-email"
                            label="דואר אלקטרוני"
                            value={user?.email ?? ''}
                            disabled
                        />
                        <TextField
                            id="user-first-name"
                            label="שם פרטי"
                            value={user?.firstName ?? ''}
                            disabled
                        />
                        <TextField
                            id="user-last-name"
                            label="שם משפחה"
                            value={user?.lastName ?? ''}
                            disabled
                        />
                    </Stack>
                </Section>

                <Section title="הרשאות ושיוך" xs={12} md={6}>
                    <Stack spacing={2}>
                        <FormControlLabel
                            label="חסום"
                            control={
                                <Checkbox
                                    checked={blocked}
                                    onChange={(event) => setBlocked(event.target.checked)}
                                />
                            }
                        />
                        <TextField
                            id="user-firm"
                            select
                            label="משרד"
                            value={firmId}
                            onChange={(event) => setFirmId(event.target.value)}
                            slotProps={{ select: { native: true } }}
                        >
                            <option value={NO_FIRM}>ללא משרד</option>
                            {(firms ?? []).map((firm) => (
                                <option key={firm.id} value={firm.id}>{firm.name}</option>
                            ))}
                        </TextField>
                    </Stack>
                </Section>
            </AppFormContainer>
        </Page>
    )
}
